import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import Modal from '../../../../components/Modal';
import TextField from '../../../../components/TextField';
import Button from '../../../../components/Button';
import { FiEdit2, FiX } from 'react-icons/fi';
import Icon from '../../../../components/Icon';
import type { Company } from '../../service/companiesService';
import { updateUserEmail } from '../../../../services/passwordService';

type CompanyDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    company: Company | null;
    bankId?: string;
    onUpdate?: () => void;
};

export default function CompanyDetailsModal({ isOpen, onClose, company, bankId: _bankId, onUpdate }: CompanyDetailsModalProps) {
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editedCompany, setEditedCompany] = useState<Partial<Company>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Secondary email chip state
    const [secondaryEmails, setSecondaryEmails] = useState<string[]>([]);
    const [emailInput, setEmailInput] = useState('');
    const [emailError, setEmailError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setIsEditing(false);
            setEditedCompany({});
            setErrors({});
            setSecondaryEmails([]);
            setEmailInput('');
            setEmailError(null);
        } else if (company) {
            setEditedCompany({
                companyName: company.companyName || '',
                companyType: company.companyType || '',
                companyId: company.companyId || '',
                companyNo: company.companyNo || '',
                email: company.email || '',
                companyAddress: company.companyAddress || '',
                contactPersonName: company.contactPersonName || '',
                contactMobileNumber: company.contactMobileNumber || '',
                contactEmail: company.contactEmail || '',
                status: company.status || 'Active',
            });
            setSecondaryEmails((company as any).secondaryEmails || []);
        }
    }, [isOpen, company]);

    const handleInputChange = (field: string, value: string) => {
        setEditedCompany(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    // ── Secondary email chip helpers ──
    const validateEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

    const handleAddEmail = () => {
        const normalized = emailInput.trim().toLowerCase();
        if (!normalized) return;
        if (!validateEmail(normalized)) { setEmailError('Enter a valid email address.'); return; }
        if (secondaryEmails.includes(normalized)) { setEmailError('Email already added.'); return; }
        setSecondaryEmails(prev => [...prev, normalized]);
        setEmailInput('');
        setEmailError(null);
    };

    const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAddEmail(); }
    };

    const handleRemoveEmail = (email: string) => {
        setSecondaryEmails(prev => prev.filter(em => em !== email));
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!editedCompany.companyName?.trim() || !editedCompany.companyType) {
            newErrors.submit = 'Please fill in company name and type.';
            setErrors(newErrors);
            return false;
        }
        setErrors(newErrors);
        return true;
    };

    const handleSave = async () => {
        if (!company) return;
        if (!validateForm()) return;

        setLoading(true);
        try {
            const nextEmail = (editedCompany.email ?? '').toString().trim();
            const prevEmail = (company.email ?? '').toString().trim();
            const authUid = (company as any).authUid;

            if (nextEmail && nextEmail !== prevEmail && authUid) {
                await updateUserEmail(authUid, nextEmail);
            }

            const docId = (company as any).authUid || (company as any).userId || company.id;
            const companyRef = doc(db, 'companies', docId);

            const payload: any = {
                companyName: editedCompany.companyName?.trim(),
                companyType: editedCompany.companyType,
                companyId: editedCompany.companyId?.trim() || null,
                companyNo: editedCompany.companyNo?.trim() || null,
                email: editedCompany.email?.trim() || null,
                companyAddress: editedCompany.companyAddress?.trim() || null,
                contactPersonName: editedCompany.contactPersonName?.trim() || null,
                contactMobileNumber: editedCompany.contactMobileNumber?.trim() || null,
                contactEmail: editedCompany.contactEmail?.trim() || null,
                status: editedCompany.status || 'Active',
                secondaryEmails,
                updatedAt: new Date(),
            };

            await updateDoc(companyRef, payload);

            if (_bankId) {
                const bankCompanyRef = doc(db, 'banks', _bankId, 'companies', company.id);
                try {
                    await updateDoc(bankCompanyRef, {
                        contactPersonName: editedCompany.contactPersonName?.trim() || null,
                        contactMobileNumber: editedCompany.contactMobileNumber?.trim() || null,
                        contactEmail: editedCompany.contactEmail?.trim() || null,
                        secondaryEmails,
                        status: editedCompany.status || 'Active',
                        updatedAt: new Date(),
                    });
                } catch (bankErr) {
                    console.error('Error updating bank company doc:', bankErr);
                }
            }

            setIsEditing(false);
            if (onUpdate) onUpdate();
        } catch (err: any) {
            console.error('Error updating company:', err);
            setErrors({ submit: err.message || 'Failed to update company' });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (company) {
            setEditedCompany({
                companyName: company.companyName || '',
                companyType: company.companyType || '',
                companyId: company.companyId || '',
                companyNo: company.companyNo || '',
                email: company.email || '',
                companyAddress: company.companyAddress || '',
                contactPersonName: company.contactPersonName || '',
                contactMobileNumber: company.contactMobileNumber || '',
                contactEmail: company.contactEmail || '',
                status: company.status || 'Active',
            });
            setSecondaryEmails((company as any).secondaryEmails || []);
        }
        setEmailInput('');
        setEmailError(null);
        setErrors({});
        setIsEditing(false);
    };

    const handleClose = () => {
        if (isEditing) handleCancel();
        onClose();
    };

    if (!company) return null;

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Company Details" size="lg">
            <div className="p-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Edit Button */}
                {isBankUser && !isEditing && (
                    <div className="mb-4 flex justify-end">
                        <button
                            onClick={() => setIsEditing(true)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                            aria-label="Edit company details"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                    </div>
                )}

                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <TextField
                            label="Company Name *"
                            value={editedCompany.companyName ?? company.companyName ?? ''}
                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                            readOnly={!isBankUser || !isEditing}
                        />
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Company Type *
                            </label>
                            <input
                                type="text"
                                value={editedCompany.companyType ?? company.companyType ?? ''}
                                readOnly
                                className="w-full rounded-xl border border-gray-300 bg-gray-100 py-2.5 px-4 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <TextField
                            label="Company ID"
                            value={editedCompany.companyId ?? company.companyId ?? ''}
                            onChange={(e) => handleInputChange('companyId', e.target.value)}
                            placeholder="Enter company ID (e.g., MG-001)"
                            readOnly={!isBankUser || !isEditing}
                        />
                        <TextField
                            label="Phone Number"
                            type="tel"
                            value={editedCompany.companyNo ?? company.companyNo ?? ''}
                            onChange={(e) => handleInputChange('companyNo', e.target.value)}
                            placeholder="Enter phone number"
                            readOnly={!isBankUser || !isEditing}
                        />
                    </div>

                    <TextField
                        label="Email"
                        type="email"
                        value={editedCompany.email ?? company.email ?? ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter email address"
                        readOnly={!isBankUser || !isEditing}
                    />

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Company Address
                        </label>
                        <textarea
                            value={editedCompany.companyAddress ?? company.companyAddress ?? ''}
                            onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                            placeholder="Enter company address"
                            readOnly={!isBankUser || !isEditing}
                            rows={3}
                            className="w-full rounded-xl border py-3 px-4 text-sm outline-none transition bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 read-only:bg-gray-50 dark:read-only:bg-gray-800/50"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <TextField
                            label="Contact Person Name"
                            value={editedCompany.contactPersonName ?? company.contactPersonName ?? ''}
                            onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                            placeholder="Enter contact person name"
                            readOnly={!isBankUser || !isEditing}
                        />
                        <TextField
                            label="Contact Mobile Number"
                            type="tel"
                            value={editedCompany.contactMobileNumber ?? company.contactMobileNumber ?? ''}
                            onChange={(e) => handleInputChange('contactMobileNumber', e.target.value)}
                            placeholder="Enter contact mobile number"
                            readOnly={!isBankUser || !isEditing}
                        />
                    </div>

                    <TextField
                        label="Contact Email"
                        type="email"
                        value={editedCompany.contactEmail ?? company.contactEmail ?? ''}
                        onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                        placeholder="Enter contact email address"
                        readOnly={!isBankUser || !isEditing}
                    />

                    {/* ── Secondary Emails Chip Input ── */}
                    <div>
                        <span className="mb-1 block text-xs font-normal uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Secondary Emails
                        </span>
                        <div className={`rounded-xl border p-3 ${isEditing ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700' : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'}`}>
                            <div className="flex flex-wrap gap-2">
                                {secondaryEmails.map((em) => (
                                    <span key={em} className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100">
                                        {em}
                                        {isEditing && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveEmail(em)}
                                                className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                                                aria-label={`Remove ${em}`}
                                            >
                                                <Icon icon={FiX} className="h-3 w-3" />
                                            </button>
                                        )}
                                    </span>
                                ))}
                                {isEditing && (
                                    <input
                                        type="email"
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        onKeyDown={handleEmailKeyDown}
                                        onBlur={handleAddEmail}
                                        placeholder={secondaryEmails.length > 0 ? 'Add another email...' : 'Type email and press Enter'}
                                        className="flex-1 min-w-[200px] border-none bg-transparent text-sm text-gray-700 outline-none dark:text-gray-100"
                                    />
                                )}
                                {!isEditing && secondaryEmails.length === 0 && (
                                    <span className="text-sm text-gray-400 italic">No secondary emails</span>
                                )}
                            </div>
                        </div>
                        {emailError && <p className="mt-1 text-xs text-red-500">{emailError}</p>}
                    </div>

                    {isEditing && (
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                            <select
                                value={editedCompany.status ?? company.status ?? 'Active'}
                                onChange={(e) => handleInputChange('status', e.target.value)}
                                className="form-control h-9 py-0 pl-3 pr-8 !text-xs"
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="Suspended">Suspended</option>
                            </select>
                        </div>
                    )}

                    {errors.submit && (
                        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
                            {errors.submit}
                        </div>
                    )}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    {isEditing ? (
                        <>
                            <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
}
